import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULTS = {
  senderLine1: "Tobias Boldt",
  senderLine2: "Mozartstr. 10a",
  senderLine3: "40764 Langenfeld",
  senderEmail: SHOP.email,
  vatId: "DE286520763",
  vatRate: 19,
  invoicePrefix: "Etsy -",
  footerPaymentText:
    "Ihre Bestellung wird nach Eingang der Zahlung bearbeitet. " +
    "Bitte überweisen Sie den Gesamtbetrag unter Angabe der Rechnungsnummer.",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) =>
  Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (ts) => {
  const d = ts instanceof Date ? ts : new Date(ts * 1000);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const paymentLabel = (method) => {
  if (!method) return "Etsy Payments";
  const m = method.toLowerCase();
  if (m === "paypal") return "PayPal";
  if (m === "check") return "Scheck";
  return "Etsy Payments";
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// ── Invoice HTML builder ──────────────────────────────────────────────────────

function buildInvoiceHtml(orderData, receiptData, cfg, invoiceDate, shippingDate) {
  const {
    senderLine1, senderLine2, senderLine3, senderEmail,
    vatId, vatRate, invoicePrefix, footerPaymentText,
  } = cfg;

  const orderId = orderData.orderId || orderData.id;
  const invoiceRef = `${invoicePrefix} ${orderId}`;
  const orderDate = fmtDate(receiptData.createTimestamp);
  const payDate   = fmtDate(receiptData.createTimestamp);
  const invDate   = fmtDate(new Date(invoiceDate));
  const shipDate  = shippingDate ? fmtDate(new Date(shippingDate)) : "—";

  // Address block
  const addr = orderData.address || {};
  const addrLines = [
    orderData.buyerName,
    addr.street ? `${addr.street}${addr.house ? " " + addr.house : ""}` : addr.line1,
    addr.line2,
    `${addr.zip || addr.postalCode || ""} ${addr.city || ""}`.trim(),
    addr.country && addr.country !== "DE" ? addr.country : "",
  ].filter(Boolean);

  // Items from Firestore + prices from Etsy transactions
  const items = orderData.items || [];
  const txns  = receiptData.transactions || [];
  const vatRateNum = Number(vatRate) || 19;
  const vatFactor  = 1 + vatRateNum / 100;

  const grandTotal  = receiptData.grandTotal || 0;
  const shipping    = receiptData.shipping || 0;
  const discountAmt = receiptData.discountAmt || 0;
  const subtotal    = receiptData.subtotal || 0;   // after discount, before shipping (gross)

  // All amounts shown as net (gross / vatFactor)
  const netSubtotal = subtotal / vatFactor;
  const netShipping = shipping / vatFactor;
  const netTotal    = netSubtotal + netShipping;
  const vatAmount   = (grandTotal / vatFactor) * (vatRateNum / 100);

  // Per-item net price from transactions (matched by index)
  const itemRows = items.map((item, i) => {
    const qty         = item.qty || item.quantity || 1;
    const grossUnit   = txns[i] ? txns[i].price : (subtotal / Math.max(items.length, 1));
    const netUnit     = grossUnit / vatFactor;
    const lineTotal   = netUnit * qty;
    const title       = item.titleDe || item.title || item.name || "—";
    const variant     = item.variant ? `Variante: ${item.variant}` : "";
    const pers        = item.personalization ? `Personalisierung: ${item.personalization}` : "";
    const sub         = [variant, pers].filter(Boolean).join(" | ");
    return { pos: i + 1, title, sub, qty, netUnit, lineTotal };
  });

  // Preisnachlass row (net) — only when a discount was applied
  const discountNetAmt = discountAmt / vatFactor;
  const nextPos        = itemRows.length + 1;

  const itemRowsHtml = [
    ...itemRows.map((r) => `
    <tr>
      <td style="text-align:center">${r.pos}</td>
      <td></td>
      <td>
        ${r.title}
        ${r.sub ? `<br><span style="font-size:0.85em;color:#555">${r.sub}</span>` : ""}
      </td>
      <td style="text-align:center">${r.qty}</td>
      <td style="text-align:right">${fmt(r.netUnit)} €</td>
      <td style="text-align:right">${fmt(r.lineTotal)} €</td>
    </tr>`),
    discountAmt > 0 ? `
    <tr>
      <td style="text-align:center">${nextPos}</td>
      <td></td>
      <td>Preisnachlass</td>
      <td></td>
      <td></td>
      <td style="text-align:right">-${fmt(discountNetAmt)} €</td>
    </tr>` : "",
  ].join("");

  const esc = (s) => String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>Rechnung_${orderId}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11pt; color: #222; }
  .page { width: 210mm; margin: 0 auto; padding: 18mm 18mm 12mm 18mm; }
  .logo { float: right; max-width: 110px; margin: 0 0 6mm 10mm; }
  .sender-line { font-size: 8pt; color: #555; text-decoration: underline; margin-bottom: 4mm; }
  .address-block { font-size: 11pt; line-height: 1.6; margin-bottom: 6mm; }
  h1 { font-size: 18pt; font-weight: 700; margin-bottom: 3mm; clear: both; }
  .meta-table { border-collapse: collapse; margin-bottom: 6mm; }
  .meta-table td { padding: 2px 20px 2px 0; font-size: 10pt; vertical-align: top; }
  .meta-table td:first-child { color: #555; white-space: nowrap; }
  .items-table { width: 100%; border-collapse: collapse; margin-bottom: 5mm; font-size: 10pt; }
  .items-table th { background: #f5f5f5; border: 1px solid #ccc; padding: 4px 6px; text-align: left; font-weight: 600; }
  .items-table td { border: 1px solid #ccc; padding: 3px 6px; vertical-align: top; }
  .totals-table { float: right; border-collapse: collapse; font-size: 10pt; margin-bottom: 3mm; }
  .totals-table td { padding: 2px 4px; }
  .totals-table td:first-child { color: #555; padding-right: 20px; }
  .totals-table td:last-child { text-align: right; white-space: nowrap; }
  .totals-total td { font-weight: 700; font-size: 11pt; border-top: 1px solid #ccc; padding-top: 3px; }
  .clearfix::after { content: ""; display: table; clear: both; }
  .vat-note { font-size: 9pt; color: #555; margin-bottom: 5mm; }
  .footer-text { font-size: 9.5pt; line-height: 1.5; margin-bottom: 3mm; white-space: pre-wrap; }
  .bank-block { font-size: 9.5pt; line-height: 1.6; margin-bottom: 5mm; }
  .page-footer { border-top: 1px solid #ccc; padding-top: 3mm; font-size: 8.5pt; color: #555; text-align: center; }
  @media print {
    @page { margin: 0; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 14mm 15mm 10mm 15mm; }
  }
</style>
</head>
<body>
<div class="page">

  <!-- Logo floats right alongside sender line + address -->
  <img class="logo" src="${SHOP.logo}" alt="${SHOP.name}" onerror="this.style.display='none'">

  <!-- Sender line -->
  <div class="sender-line">${esc(senderLine1)} · ${esc(senderLine2)} · ${esc(senderLine3)}</div>

  <!-- Customer address -->
  <div class="address-block">
    ${addrLines.map(esc).join("<br>")}
  </div>

  <h1>Rechnung</h1>
  <hr style="border:none;border-top:1px solid #ccc;margin-bottom:4mm">

  <!-- Invoice metadata -->
  <table class="meta-table">
    <tr><td>Rechnung</td><td>${esc(invoiceRef)}</td></tr>
    <tr><td>Rechnungsdatum</td><td>${esc(invDate)}</td></tr>
    <tr><td>Bestelldatum</td><td>${esc(orderDate)}</td></tr>
    <tr><td>Zahldatum</td><td>${esc(payDate)}</td></tr>
    <tr><td>Versanddatum</td><td>${esc(shipDate)}</td></tr>
    <tr><td>Zahlart</td><td>${esc(paymentLabel(receiptData.paymentMethod))}</td></tr>
    <tr><td>Referenz</td><td>${esc(invoiceRef)}</td></tr>
  </table>

  <!-- Line items -->
  <table class="items-table">
    <thead>
      <tr>
        <th style="width:5%;text-align:center">Pos</th>
        <th style="width:10%">Nummer</th>
        <th>Artikel</th>
        <th style="width:8%;text-align:center">Menge</th>
        <th style="width:12%;text-align:right">Preis</th>
        <th style="width:12%;text-align:right">Summe</th>
      </tr>
    </thead>
    <tbody>
      ${itemRowsHtml}
    </tbody>
  </table>

  <!-- Totals -->
  <div class="clearfix">
    <table class="totals-table">
      <tr><td>Zwischensumme (netto)</td><td>${fmt(netSubtotal)} €</td></tr>
      <tr><td>Versand (netto)</td><td>${fmt(netShipping)} €</td></tr>
      <tr><td>Gesamt netto</td><td>${fmt(netTotal)} €</td></tr>
      <tr>
        <td>Umsatzsteuer (${vatRateNum.toFixed(1).replace(".", ",")} %)</td>
        <td>${fmt(vatAmount)} €</td>
      </tr>
      <tr class="totals-total"><td>Gesamtsumme</td><td>${fmt(grandTotal)} €</td></tr>
    </table>
  </div>
  <p class="vat-note" style="margin-top:2mm">inkl. MwSt.</p>

  <!-- Footer payment text -->
  <div class="footer-text">${esc(footerPaymentText)}</div>

  <!-- Page footer -->
  <div class="page-footer">
    ${esc(senderLine1)} · ${esc(senderLine2)} · ${esc(senderLine3)}<br>
    E-Mail: ${esc(senderEmail)} &nbsp;|&nbsp; Umsatzsteuer-IdNr: ${esc(vatId)}
  </div>

</div>
<script>window.onload = function() { window.print(); };</script>
</body>
</html>`;
}

// ── Vorlage tab ───────────────────────────────────────────────────────────────

function VorlageTab({ config, setConfig, saving, onSave }) {
  const field = (label, key, opts = {}) => (
    <div style={{ marginBottom: "1rem" }}>
      <label style={{ display: "block", fontWeight: 500, marginBottom: 4, color: "#555", fontSize: "0.85rem" }}>
        {label}
      </label>
      {opts.textarea ? (
        <textarea
          rows={4}
          value={config[key] ?? ""}
          onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
          style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: "0.9rem", resize: "vertical" }}
        />
      ) : (
        <input
          type={opts.type || "text"}
          value={config[key] ?? ""}
          onChange={(e) => {
            const v = opts.type === "number" ? Number(e.target.value) : e.target.value;
            setConfig((c) => ({ ...c, [key]: v }));
          }}
          style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.9rem" }}
        />
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 560 }}>
      <h3 style={{ marginBottom: "1.2rem", color: "#4a2c2a" }}>Absender</h3>
      {field("Name", "senderLine1")}
      {field("Straße", "senderLine2")}
      {field("PLZ / Ort", "senderLine3")}
      {field("E-Mail", "senderEmail")}

      <h3 style={{ margin: "1.5rem 0 1.2rem", color: "#4a2c2a" }}>Steuer</h3>
      {field("Umsatzsteuer-IdNr.", "vatId")}
      {field("MwSt-Satz (%)", "vatRate", { type: "number" })}

      <h3 style={{ margin: "1.5rem 0 1.2rem", color: "#4a2c2a" }}>Rechnungs-Präfix</h3>
      {field('Präfix (z. B. „Etsy -“)', "invoicePrefix")}

      <h3 style={{ margin: "1.5rem 0 1.2rem", color: "#4a2c2a" }}>Zahlungshinweis / Fußtext</h3>
      {field("Fußtext", "footerPaymentText", { textarea: true })}

      <button
        onClick={onSave}
        disabled={saving}
        style={{
          marginTop: "0.5rem", padding: "0.55rem 1.4rem",
          background: saving ? "#a0aec0" : "#4a2c2a", color: "#fff",
          border: "none", borderRadius: 6, cursor: saving ? "default" : "pointer",
          fontWeight: 600, fontSize: "0.9rem",
        }}
      >
        {saving ? "Speichern…" : "Speichern"}
      </button>
    </div>
  );
}

// ── Erstellen tab ─────────────────────────────────────────────────────────────

function ErstellenTab({ config }) {
  const [orderId, setOrderId]       = useState("");
  const [invDate, setInvDate]       = useState(todayIso());
  const [shipDate, setShipDate]     = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [orderData, setOrderData]   = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  const handleLoad = async () => {
    const id = orderId.trim();
    if (!id) { setError("Bitte Bestellnummer eingeben."); return; }
    setLoading(true);
    setError("");
    setOrderData(null);
    setReceiptData(null);
    try {
      const snap = await getDoc(doc(db, "etsy_orders", id));
      if (!snap.exists()) throw new Error(`Bestellung ${id} nicht gefunden.`);
      const od = { id, ...snap.data() };

      // Pre-fill shipping date from labelCreatedAt
      if (od.labelCreatedAt) {
        const ts = od.labelCreatedAt.seconds
          ? od.labelCreatedAt.seconds
          : Math.floor(new Date(od.labelCreatedAt).getTime() / 1000);
        const d = new Date(ts * 1000);
        setShipDate(d.toISOString().slice(0, 10));
      }

      const fn = getFunctions(undefined, "europe-west1");
      const getReceiptData = httpsCallable(fn, "getReceiptData");
      const res = await getReceiptData({ orderId: id });

      setOrderData(od);
      setReceiptData(res.data);
    } catch (e) {
      setError(e.message || "Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  };

  const handlePdf = () => {
    const html = buildInvoiceHtml(orderData, receiptData, config, invDate, shipDate || null);
    const win = window.open("", "_blank");
    if (!win) { alert("Bitte Pop-ups für diese Seite erlauben."); return; }
    win.document.write(html);
    win.document.close();
  };

  const fmt2 = (n) => fmt(n);

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 4, color: "#555", fontSize: "0.85rem" }}>
            Bestellnummer
          </label>
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLoad()}
            placeholder="z. B. 4035530641"
            style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.9rem" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 4, color: "#555", fontSize: "0.85rem" }}>
            Rechnungsdatum
          </label>
          <input
            type="date"
            value={invDate}
            onChange={(e) => setInvDate(e.target.value)}
            style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.9rem" }}
          />
        </div>
      </div>

      <button
        onClick={handleLoad}
        disabled={loading}
        style={{
          padding: "0.55rem 1.4rem", marginBottom: "1rem",
          background: loading ? "#a0aec0" : "#2d6a4f", color: "#fff",
          border: "none", borderRadius: 6, cursor: loading ? "default" : "pointer",
          fontWeight: 600, fontSize: "0.9rem",
        }}
      >
        {loading ? "Lade Daten…" : "Daten laden"}
      </button>

      {error && (
        <div style={{ background: "#fff5f5", border: "1px solid #feb2b2", borderRadius: 6, padding: "0.6rem 0.9rem", color: "#c53030", marginBottom: "1rem", fontSize: "0.9rem" }}>
          {error}
        </div>
      )}

      {orderData && receiptData && (
        <>
          <div style={{ background: "#f9fafb", border: "1px solid #e2e8f0", borderRadius: 8, padding: "1rem 1.2rem", marginBottom: "1rem" }}>
            <div style={{ fontWeight: 600, marginBottom: "0.5rem", color: "#1a202c" }}>
              {orderData.buyerName} — Bestellung #{orderData.id}
            </div>

            {/* Address */}
            {orderData.address && (
              <div style={{ fontSize: "0.875rem", color: "#555", marginBottom: "0.5rem", lineHeight: 1.5 }}>
                {[
                  orderData.address.street && `${orderData.address.street}${orderData.address.house ? " " + orderData.address.house : ""}`,
                  `${orderData.address.zip || orderData.address.postalCode || ""} ${orderData.address.city || ""}`.trim(),
                ].filter(Boolean).join(", ")}
              </div>
            )}

            {/* Items */}
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "0.5rem", marginTop: "0.5rem" }}>
              {(orderData.items || []).map((item, i) => (
                <div key={i} style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                  <span style={{ color: "#2d6a4f", fontWeight: 500 }}>×{item.qty || item.quantity || 1}</span>{" "}
                  {item.titleDe || item.title || item.name}
                  {receiptData.transactions?.[i] && (
                    <span style={{ color: "#718096", marginLeft: 8 }}>
                      {fmt2(receiptData.transactions[i].price)} €
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "0.5rem", marginTop: "0.5rem", fontSize: "0.875rem" }}>
              {receiptData.discountAmt > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#c53030" }}>Preisnachlass</span>
                  <span style={{ color: "#c53030" }}>-{fmt2(receiptData.discountAmt)} €</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#555" }}>Versand</span>
                <span>{fmt2(receiptData.shipping)} €</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 4 }}>
                <span>Gesamt</span>
                <span>{fmt2(receiptData.grandTotal)} €</span>
              </div>
            </div>
          </div>

          {/* Versanddatum field — shown after load */}
          <div style={{ marginBottom: "1rem", maxWidth: 220 }}>
            <label style={{ display: "block", fontWeight: 500, marginBottom: 4, color: "#555", fontSize: "0.85rem" }}>
              Versanddatum
            </label>
            <input
              type="date"
              value={shipDate}
              onChange={(e) => setShipDate(e.target.value)}
              style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.9rem" }}
            />
          </div>

          <button
            onClick={handlePdf}
            style={{
              padding: "0.55rem 1.4rem",
              background: "#4a2c2a", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              fontWeight: 600, fontSize: "0.9rem",
            }}
          >
            PDF herunterladen
          </button>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function InvoiceSettings() {
  const [activeTab, setActiveTab] = useState("erstellen");
  const [config, setConfig]       = useState(DEFAULTS);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    getDoc(doc(db, "config", "invoiceSettings")).then((snap) => {
      if (snap.exists()) setConfig({ ...DEFAULTS, ...snap.data() });
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await setDoc(doc(db, "config", "invoiceSettings"), config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const tab = (id, label) => (
    <button
      key={id}
      onClick={() => setActiveTab(id)}
      style={{
        padding: "0.5rem 1.2rem", border: "none", cursor: "pointer",
        fontWeight: activeTab === id ? 700 : 500,
        fontSize: "0.9rem",
        color: activeTab === id ? "#4a2c2a" : "#718096",
        background: activeTab === id ? "#fff" : "transparent",
        borderBottom: activeTab === id ? "2px solid #4a2c2a" : "2px solid transparent",
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ marginBottom: "1.5rem", color: "#1a202c", fontWeight: 700 }}>
        Rechnungen
      </h2>

      {saved && (
        <div style={{ background: "#f0fff4", border: "1px solid #9ae6b4", borderRadius: 6, padding: "0.5rem 0.9rem", color: "#276749", marginBottom: "1rem", fontSize: "0.9rem" }}>
          Einstellungen gespeichert.
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: "1.5rem" }}>
        {tab("vorlage", "Vorlage")}
        {tab("erstellen", "Erstellen")}
      </div>

      {activeTab === "vorlage" && (
        <VorlageTab config={config} setConfig={setConfig} saving={saving} onSave={handleSave} />
      )}
      {activeTab === "erstellen" && (
        <ErstellenTab config={config} />
      )}
    </div>
  );
}
